/**
 * ================================================================================================
 *  WHICH EXCHANGE RATES THIS PERSON WANTS ON THE CARD. **Owner, 2026-11-01.**
 * ================================================================================================
 *
 *     *"אני רוצה לבחור איזה שער להוסיף. אני רוצה להוסיף את ה־4 מדדים שהיו. כאשר סידרת את הגופן
 *     אין בעיה להוסיף עוד."*
 *
 * ── IT WAS A CONSTANT THIS MORNING AND IT SHOULD NOT HAVE BEEN ────────────────────────────────
 *
 * The card showed whatever the publisher sent, then the two he named, then — on the strength of
 * *"מספיק לי דולר ואירו"* — only those two, as a list in the component. Both of those were me
 * deciding for him in code what is a choice: *"אני רוצה לבחור"* says so, and the font work he
 * asked for in between is what made room for the answer to be more than two.
 *
 * ── IT IS THE FIFTH PREFERENCE AND NOT A FIFTH MECHANISM ──────────────────────────────────────
 *
 * The same store as the home order, the hidden set and the favourites: per user, across devices,
 * device first and the account after. A list of currency codes is a short list of distinct code
 * names, which is exactly what `orderList` already validates — so no new rule either.
 *
 * ── IT HOLDS WHAT IS SHOWN, WHICH IS THE OPPOSITE OF `home.hidden` ───────────────────────────
 *
 * The hidden set holds EXCLUSIONS so that a tile a later release adds is visible to everybody.
 * This holds INCLUSIONS, and the difference is the Owner's sentence: he wants to CHOOSE what to
 * add. A currency the Bank of Israel starts publishing tomorrow must not appear on his card
 * because they published it — it appears when he picks it.
 *
 * AND AN EMPTY STORED LIST IS NOT "NONE". Nothing stored means the default below, which is the
 * four the source has always sent; a person who unticks everything gets the default back rather
 * than an empty card, because an empty card is indistinguishable from a broken one.
 */

import { readOnDevice, rememberPreference } from './preferences';

/** The one preference this module names. Where it is kept is `preferences.ts`'s business. */
const KEY = 'home.currencies' as const;

/**
 * THE FOUR THE SOURCE HAS ALWAYS SENT, in the order an Israeli practice reads them.
 *
 * *"אני רוצה להוסיף את ה־4 מדדים שהיו"* — the dollar, the euro, sterling and the yen. It is a
 * DEFAULT and not a limit: anything the publisher sends can be chosen, and `CURRENCY_NAME` in
 * `MarketTile` is what decides whether it can be named in Hebrew as well as coded.
 */
export const DEFAULT_CURRENCIES: readonly string[] = ['USD', 'EUR', 'GBP', 'JPY'];

/** Same-tab subscribers. `storage` fires in OTHER tabs only, so the writer tells this one itself. */
const listeners = new Set<(value: readonly string[]) => void>();

/** What this person chose, or the default. Never an empty list — see the header. */
export function chosenCurrencies(): readonly string[] {
  const stored = readOnDevice(KEY);
  return stored === null || stored.length === 0 ? DEFAULT_CURRENCIES : stored;
}

/**
 * ADD OR REMOVE ONE, AND ANSWER WITH THE NEW LIST.
 *
 * The order is the DEFAULT's and not the click order: a person ticking the yen before the dollar
 * is choosing a set, not an arrangement, and a card whose rows moved according to the order
 * somebody happened to tick them in would be a card that cannot be scanned twice.
 */
export function setCurrencyShown(code: string, shown: boolean): readonly string[] {
  const current = new Set(chosenCurrencies());
  if (shown) current.add(code);
  else current.delete(code);
  const next = DEFAULT_CURRENCIES.filter((c) => current.has(c)).concat(
    [...current].filter((c) => !DEFAULT_CURRENCIES.includes(c)).sort(),
  );
  rememberPreference(KEY, next);
  for (const listener of listeners) listener(next);
  return next;
}

/** A change the account brought back, pushed in by whoever called `hydratePreferences`. */
export function currenciesChangedOnAccount(): void {
  const value = chosenCurrencies();
  for (const listener of listeners) listener(value);
}

/** Subscribe to changes made in this tab. Returns the unsubscribe. */
export function onCurrenciesChanged(listener: (value: readonly string[]) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
